import { useEffect, useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { notifications } from '@mantine/notifications';
import { Container, Center, Loader, Text, Stack, Box } from '@mantine/core';
import { File as FileComponent } from './components/File';
import { File as FileType } from './types';
import { Folder as FolderType } from '@/users/components/UserFolders/types';
import { FolderMenu } from './components/FolderMenu';
import { NewFilesAdded } from './components/NewFilesAdded';
import routes from '@commons/routes';
import { useAppDispatch, useAppSelector } from '@store';
import {
  fetchClientFolder,
  updateClientFile,
} from '@store/slices/foldersSlice';
import { selectFoldersLoading } from '@store/selectors/foldersSelectors';
import { PreviewFileModal } from '@/users/components/UserFolders/components/Modals/PreviewFileModal';

export const FolderPage = () => {
  let { folderName } = useParams();
  folderName = folderName?.toUpperCase();
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectFoldersLoading);
  const allFolders = useAppSelector((state) => state.folders.folders);
  const folder = useMemo(
    () =>
      allFolders.find(
        (f: any) => f.folderName === folderName || f.id === folderName
      ),
    [allFolders, folderName]
  );

  const [previewItem, setPreviewItem] = useState<FileType | null>(null);

  useEffect(() => {
    if (!folderName) return;

    // Always fetch to ensure we have the latest data
    dispatch(fetchClientFolder({ folderName }))
      .unwrap()
      .catch((error: any) => {
        if (error?.status === 401) {
          navigate(routes.routes.clientLogin(folderName as string));
        } else if (error?.status === 403) {
          // Access denied - redirect to access page
          navigate(routes.routes.clientLogin(folderName as string));
        } else {
          notifications.show({
            color: 'red',
            title: intl.formatMessage({ id: 'error' }),
            message:
              error?.message ||
              intl.formatMessage({ id: 'failedToLoadFolder' }),
          });
        }
      });
  }, [folderName, navigate, dispatch, intl]);

  const handleConfirm = useCallback(
    async (_file: FileType) => {
      if (!folder || !folderName) return;

      try {
        // Optimistic update via Redux
        await dispatch(
          updateClientFile({
            folderId: folder.id,
            fileId: _file.id,
            updates: {
              isVerified: true,
            },
          })
        ).unwrap();
      } catch (error: any) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message:
            error.message || intl.formatMessage({ id: 'failedToConfirmFile' }),
          color: 'red',
          autoClose: 3000,
        });
      }
    },
    [folder, folderName, dispatch, navigate, intl]
  );

  const handleEdit = useCallback(
    (_file: FileType) => {
      if (!folderName) {
        return;
      }

      navigate(routes.routes.folderFiles(folderName, _file.id));
    },
    [folderName, navigate]
  );

  const handlePreview = useCallback((_file: FileType) => {
    setPreviewItem(_file);
  }, []);

  if (isLoading) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  if (!folder) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Text c="dimmed">
            {intl.formatMessage({ id: 'failedToLoadFolder' })}
          </Text>
        </Center>
      </Container>
    );
  }

  return (
    <Container size={600} my="md">
      <NewFilesAdded folder={folder as FolderType} />

      <Box my="md">
        <FolderMenu folderName={folderName as string} />
      </Box>

      <Stack gap="md">
        {(folder?.files as FileType[] | undefined)?.map(
          (file: FileType, fileIndex: number) => (
            <FileComponent
              key={fileIndex}
              file={file}
              onPreview={() => handlePreview(file)}
              onEdit={() => handleEdit(file)}
              onConfirm={() => handleConfirm(file)}
            />
          )
        )}
      </Stack>

      {folder?.files?.length === 0 && !isLoading && (
        <Center h={200}>
          <Text>{intl.formatMessage({ id: 'noPublishedDocuments' })}</Text>
        </Center>
      )}

      {previewItem && (
        <PreviewFileModal
          opened={!!previewItem}
          onClose={() => setPreviewItem(null)}
          folderId={folder?.id as string}
          fileId={previewItem?.id as string}
          fileDisplayName={previewItem?.name as string}
        />
      )}
    </Container>
  );
};
